import { useId } from 'react';
import { AgxCircleIcon, AgxDiagonalUpArrowIcon } from '@urbanx/agx-ui-components';
import { Provider, useGetCrmListingByCampaignId } from '@urbanx/agx.crm.client';
import { AgxBadge, AgxTooltip } from '@urbanx/agx.ui-components2';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import './CrmListingBadge.scss';

interface CrmListingBadgeProps {
  agencyId?: string;
  campaignId?: string;
}

const providerNames: Record<Provider, string> = {
  [Provider.Rex]: 'Rex',
  [Provider.Vault]: 'Vault',
  [Provider.Agentbox]: 'Agentbox',
  [Provider.BoxAndDice]: 'Box and Dice',
};

const CrmListingBadge = ({ agencyId, campaignId }: CrmListingBadgeProps) => {
  const tooltipId = useId();
  const reference = useGetCrmListingByCampaignId(
    agencyId ?? '',
    campaignId ?? '',
    Boolean(agencyId && campaignId)
  );

  if (!agencyId || !campaignId || reference.isPending) return null;

  const provider = reference.data?.activeProvider;
  const listing = reference.isError ? undefined : reference.data;
  const providerName = provider ? providerNames[provider] : 'CRM';
  const listingProvider = listing?.provider;
  const listingName = listingProvider ? providerNames[listingProvider] : undefined;
  const listingId = listing?.listingId;
  const connected = Boolean(listingName && listingId && listing?.isCurrentConnection);
  const link = connected ? listing?.url : undefined;

  const text = reference.isError
    ? `${providerName} unavailable`
    : listingName && listingId
      ? `${listingName} ${listingId}`
      : provider
        ? `${providerName} not linked`
        : 'CRM disconnected';
  const colour = connected ? tokens.fg.success : tokens.text.error;

  const badge = (
    <AgxBadge
      type="colourfill"
      size="md"
      colour="lightgrey"
      showBorder
      text={text}
      leftIcon={
        <span
          className="crmListingBadgeConnectionIcon"
          aria-label={connected ? `${listingName} connected` : undefined}
        >
          <AgxCircleIcon width={15} height={14} color={colour} />
        </span>
      }
      link={link}
      rightIcon={link ? <AgxDiagonalUpArrowIcon color={tokens.text.tertiary} /> : undefined}
    />
  );
  const content = <span className="crmListingBadge">{badge}</span>;

  const tooltip =
    link && listingProvider === Provider.Agentbox
      ? 'Open Agentbox — search using this listing ID.'
      : listingName && listingId && !connected
        ? provider
          ? 'Not syncing'
          : 'CRM disconnected. Not syncing.'
        : undefined;

  if (!tooltip) return content;

  return (
    <AgxTooltip
      id={tooltipId}
      content={tooltip}
      triggerTabIndex={link ? undefined : 0}
      triggerAriaLabel={text}
    >
      {content}
    </AgxTooltip>
  );
};

export default CrmListingBadge;
